import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Spinner } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { EmptyState } from 'src/components/molecules';
import { useCategoryConfigurationRoute } from 'src/hooks/useCategoryConfigurationRoute';
import { useTheme } from 'styled-components';
import {
  CardWrapper,
  CardsContainer,
  CardsContainerWrapper,
  CategoryConfigurationButton,
  CustomTitleWithCounter,
  EmptyStateContainer,
  LoadingContainer,
  SectionContainer,
  SectionHeader,
  ShowAllLink,
  TitleText,
} from './ActiveCategoriesSection.styles';
import { ActiveCategory, ActiveCategoryCard } from './ActiveCategoryCard';

const DEFAULT_SECTION_TITLE = 'Active Conflict of Interest Categories';
const DEFAULT_EMPTY_STATE_TITLE = 'No categories found';
const DEFAULT_FORM_MANAGEMENT_EMPTY_DESCRIPTION =
  'There are no categories configured to create a form.';
const MAX_COLLAPSED_CATEGORIES = 6;

interface Props {
  categories: ActiveCategory[];
  isLoading?: boolean;
  onShowAllCategories?: () => void;
  onCreateForm?: () => void;
  onCategoryCardClick?: (category: ActiveCategory) => void;
  sectionTitle?: string;
  sectionTitleVariant?: 'default' | 'headingH3Bold';
  emptyStateTitle?: string;
  emptyStateDescription?: React.ReactNode;
  'data-testid'?: string;
}

export const ActiveCategoriesSection = ({
  categories,
  isLoading = false,
  onShowAllCategories,
  onCreateForm,
  onCategoryCardClick,
  sectionTitle = DEFAULT_SECTION_TITLE,
  sectionTitleVariant = 'default',
  emptyStateTitle = DEFAULT_EMPTY_STATE_TITLE,
  emptyStateDescription,
  ...props
}: Props) => {
  const theme = useTheme();
  const history = useHistory();
  const categoryConfigRoute = useCategoryConfigurationRoute();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const activeCategories = useMemo(() => categories, [categories]);

  const usesFormManagementDefaultEmptyCopy = emptyStateDescription == null;

  const displayedCategories = isExpanded
    ? activeCategories
    : activeCategories.slice(0, MAX_COLLAPSED_CATEGORIES);
  const showExpandToggle =
    activeCategories.length > MAX_COLLAPSED_CATEGORIES || isExpanded;

  const handleToggleClick = () => {
    setIsExpanded((prev) => !prev);
    if (onShowAllCategories) {
      onShowAllCategories();
    }
  };

  useEffect(() => {
    const checkScrollPosition = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } =
          scrollContainerRef.current;
        const canScrollLeft = scrollLeft > 1;
        const canScrollRight = scrollLeft + clientWidth < scrollWidth - 1;
        setShowLeftGradient(canScrollLeft);
        setShowRightGradient(canScrollRight);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      checkScrollPosition();
      scrollContainer.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);

      return () => {
        scrollContainer.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [displayedCategories.length]);

  if (isLoading) {
    return (
      <SectionContainer data-testid={props['data-testid']}>
        <SectionHeader>
          <CustomTitleWithCounter data-testid={`${props['data-testid']}-title`}>
            <TitleText $variant={sectionTitleVariant}>{sectionTitle}</TitleText>
          </CustomTitleWithCounter>
        </SectionHeader>
        <LoadingContainer>
          <Spinner variant="secondary" size="large" />
        </LoadingContainer>
      </SectionContainer>
    );
  }

  if (activeCategories.length === 0) {
    return (
      <SectionContainer data-testid={props['data-testid']}>
        <SectionHeader>
          <CustomTitleWithCounter data-testid={`${props['data-testid']}-title`}>
            <TitleText $variant={sectionTitleVariant}>{sectionTitle}</TitleText>
          </CustomTitleWithCounter>
        </SectionHeader>
        <EmptyStateContainer>
          <EmptyState
            title={emptyStateTitle}
            description={
              usesFormManagementDefaultEmptyCopy
                ? DEFAULT_FORM_MANAGEMENT_EMPTY_DESCRIPTION
                : emptyStateDescription
            }
            buttonText={
              usesFormManagementDefaultEmptyCopy || !onCreateForm
                ? undefined
                : 'Create form'
            }
            onButtonClick={
              usesFormManagementDefaultEmptyCopy ? undefined : onCreateForm
            }
            renderIconButton={
              !usesFormManagementDefaultEmptyCopy && !!onCreateForm
            }
            data-testid={`${props['data-testid']}-empty-state`}
          />
          {usesFormManagementDefaultEmptyCopy && (
            <CategoryConfigurationButton
              type="button"
              onClick={() => history.push(categoryConfigRoute)}
              data-testid="form-management-category-configuration-link"
            >
              Go to category configuration
              <StyledIcon
                className="uil uil-arrow-right"
                size="24px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N0
                    : theme.atlas.semantic.color.type.inverse
                }
                aria-hidden
              />
            </CategoryConfigurationButton>
          )}
        </EmptyStateContainer>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer data-testid={props['data-testid']}>
      <SectionHeader>
        <CustomTitleWithCounter data-testid={`${props['data-testid']}-title`}>
          <TitleText $variant={sectionTitleVariant}>{sectionTitle}</TitleText>
        </CustomTitleWithCounter>
      </SectionHeader>
      <CardsContainerWrapper
        $showLeftGradient={showLeftGradient}
        $showRightGradient={showRightGradient}
        $isExpanded={isExpanded}
      >
        <CardsContainer ref={scrollContainerRef} $isExpanded={isExpanded}>
          {displayedCategories.map((category) => (
            <CardWrapper key={category.id} $isExpanded={isExpanded}>
              <ActiveCategoryCard
                category={category}
                onCardClick={
                  onCategoryCardClick
                    ? () => onCategoryCardClick(category)
                    : undefined
                }
                data-testid={`${props['data-testid']}-card-${category.id}`}
              />
            </CardWrapper>
          ))}
        </CardsContainer>
      </CardsContainerWrapper>
      {showExpandToggle && (
        <ShowAllLink
          onClick={handleToggleClick}
          aria-label={
            isExpanded ? 'Show less categories' : 'Show more categories'
          }
        >
          {!isExpanded && (
            <StyledIcon
              className="uil uil-plus"
              size="20px"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N800
                  : theme.atlas.semantic.color.type.default
              }
            />
          )}
          {isExpanded ? 'Show less' : 'Show more'}
        </ShowAllLink>
      )}
    </SectionContainer>
  );
};
